import CachedIcon from '@mui/icons-material/Cached';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import MobileScreenShareIcon from '@mui/icons-material/MobileScreenShare';
import { Button, Dialog, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns-tz';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import numberDecorator from 'src/decorators/number.decorator';
import { PlanningStatisticsEntity } from 'src/entities/PlanningStatistics.entity';

type Props = {
  planningStatistics: PlanningStatisticsEntity | undefined;
  refreshOperationStatuses: () => void;
  lastOperationStatusUpdated: string;
  isLoading: boolean;
  currentHistoryVersion: string | undefined;
  driverApp: boolean;
  co2Emissions: number;
  selectPrintButtonOnClick: () => void;
  sendOperationMail: () => void;
  selectedDeliveryIds: number[];
  onConfirmDeliveries: (deliveryIds: number[]) => void;
  onUnconfirmDeliveries: (deliveryIds: number[]) => void;
  isConfirming: boolean;
  isUnconfirming: boolean;
  billing: number;
  payment: number;
  setTariffCalculationDialogIsOpen: (open: boolean) => void;
}

const PlanningStatisticsPresenter: FC<Props> = memo((
  {
    planningStatistics,
    refreshOperationStatuses,
    lastOperationStatusUpdated,
    isLoading,
    currentHistoryVersion,
    driverApp,
    co2Emissions,
    selectPrintButtonOnClick,
    sendOperationMail,
    selectedDeliveryIds,
    onConfirmDeliveries,
    onUnconfirmDeliveries,
    isConfirming,
    isUnconfirming,
    billing,
    payment,
    setTariffCalculationDialogIsOpen,
  }
) => {
  const totalOrdersCountInitialState = '- 件';
  const totalLoadQuantityInitialState = '総数量 -';
  const totalLoadWeightKgInitialState = '総重量 - kg';
  const totalLoadVolumeM3InitialState = '総体積 - m3';
  const totalDistanceKmInitialState = '総距離 - km';
  const quantityLoadingRateInitialState = '数量積載率 - %';
  const weightLoadingRateInitialState = '重量積載率 - %';
  const volumeLoadingRateInitialState = '体積積載率 - %';
  const utilizationRateInitialState = '稼働率 - %';
  const co2InitialState = 'Co2 - t-co2';

  const [totalOrdersCount, setTotalOrdersCount] = useState(totalOrdersCountInitialState);
  const [totalLoadQuantity, setTotalLoadQuantity] = useState(totalLoadQuantityInitialState);
  const [totalLoadWeightKg, setTotalLoadWeightKg] = useState(totalLoadWeightKgInitialState);
  const [totalLoadVolumeM3, setTotalLoadVolumeM3] = useState(totalLoadVolumeM3InitialState);
  const [totalDistanceKm, setTotalDistanceKm] = useState(totalDistanceKmInitialState);
  const [quantityLoadingRate, setQuantityLoadingRate] = useState(quantityLoadingRateInitialState);
  const [weightLoadingRate, setWeightLoadingRate] = useState(weightLoadingRateInitialState);
  const [volumeLoadingRate, setVolumeLoadingRate] = useState(volumeLoadingRateInitialState);
  const [utilizationRate, setUtilizationRate] = useState(utilizationRateInitialState);
  const [co2, setCo2] = useState(co2InitialState);
  const [lastOperationStatusUpdateInitialValue, setLastOperationStatusUpdateInitialValue] = useState<string>(null);
  const [operationStatusUpdatedAgo, setOperationStatusUpdatedAgo] = useState<number>(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>(null);
  const profit = useMemo(() => billing - payment, [billing, payment]);

  const onClickTariffCalculation = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '運賃計算' });
    setTariffCalculationDialogIsOpen(true);
  }, [setTariffCalculationDialogIsOpen]);

  const totalOrdersCountInitialize = () => {
    setTotalOrdersCount(totalOrdersCountInitialState);
  };

  const totalLoadQuantityInitialize = () => {
    setTotalLoadQuantity(totalLoadQuantityInitialState);
  };

  const totalLoadWeightKgInitialize = () => {
    setTotalLoadWeightKg(totalLoadWeightKgInitialState);
  };

  const totalLoadVolumeM3Initialize = () => {
    setTotalLoadVolumeM3(totalLoadVolumeM3InitialState);
  };

  const totalDistanceKmInitialize = () => {
    setTotalDistanceKm(totalDistanceKmInitialState);
  };

  const weightLoadingRateInitialize = () => {
    setWeightLoadingRate(weightLoadingRateInitialState);
  };

  const quantityLoadingRateInitialize = () => {
    setQuantityLoadingRate(quantityLoadingRateInitialState);
  };

  const volumeLoadingRateInitialize = () => {
    setVolumeLoadingRate(volumeLoadingRateInitialState);
  };

  const utilizationRateInitialize = () => {
    setUtilizationRate(utilizationRateInitialState);
  };

  const initialize = useCallback(() => {
    totalOrdersCountInitialize();
    totalLoadQuantityInitialize();
    totalLoadWeightKgInitialize();
    totalLoadVolumeM3Initialize();
    totalDistanceKmInitialize();
    quantityLoadingRateInitialize();
    weightLoadingRateInitialize();
    volumeLoadingRateInitialize();
    utilizationRateInitialize();
  }, []);

  useEffect(() => {
    initialize();

    if (!planningStatistics) return;

    if (planningStatistics.totalOrdersCount) {
      setTotalOrdersCount(
        numberDecorator.toRoundedUnit(
          planningStatistics.totalOrdersCount,
          '件',
          1,
          ''
        )
      );
    }

    if (planningStatistics.totalLoadQuantity != null) {
      setTotalLoadQuantity(
        [
          '総数量',
          numberDecorator.formatNumber(planningStatistics.totalLoadQuantity)
        ].join(' ')
      );
    }

    if (planningStatistics.totalLoadWeightG) {
      setTotalLoadWeightKg(
        [
          '総重量',
          numberDecorator.convertGramToKg(planningStatistics.totalLoadWeightG)
        ].join('')
      );
    }

    if (planningStatistics.totalLoadVolumeMm3) {
      setTotalLoadVolumeM3(
        [
          '総体積',
          numberDecorator.convertMm3ToM3(planningStatistics.totalLoadVolumeMm3)
        ].join(' ')
      );
    }

    if (planningStatistics.totalDistanceMm) {
      setTotalDistanceKm(
        numberDecorator.toRoundedKm(
          planningStatistics.totalDistanceMm / 1000000,
          '総距離'
        )
      );
    }

    setQuantityLoadingRate(
      planningStatistics.loadCapacityQuantity > 0
        ? numberDecorator.toRoundedRate(
          planningStatistics.totalMaxLoadQuantity / planningStatistics.loadCapacityQuantity,
          '数量積載率'
        ) || quantityLoadingRateInitialState
        : quantityLoadingRateInitialState
    );

    setWeightLoadingRate(
      planningStatistics.loadCapacityWeightG > 0
        ? numberDecorator.toRoundedRate(
          planningStatistics.totalMaxLoadWeightG / planningStatistics.loadCapacityWeightG,
          '重量積載率'
        ) || weightLoadingRateInitialState
        : weightLoadingRateInitialState
    );

    setVolumeLoadingRate(
      planningStatistics.loadCapacityVolumeMm3 > 0
        ? numberDecorator.toRoundedRate(
          planningStatistics.totalMaxLoadVolumeMm3 / planningStatistics.loadCapacityVolumeMm3,
          '体積積載率'
        ) || volumeLoadingRateInitialState
        : volumeLoadingRateInitialState
    );

    setUtilizationRate(
      planningStatistics.totalPlanMinutes > 0
        ? numberDecorator.toRoundedRate(
          planningStatistics.totalWorkMinutes / planningStatistics.totalPlanMinutes,
          '稼働率'
        ) || utilizationRateInitialState
        : utilizationRateInitialState
    );
  }, [initialize, planningStatistics]);

  useEffect(() => {
    if (!co2Emissions) {
      setCo2(co2InitialState);
      return;
    }

    setCo2(
      [
        'Co2',
        numberDecorator.toRoundedUnit(co2Emissions, 't-co2', 3, '')
      ].join(' ')
    );
  }, [co2Emissions]);

  const onClickRefreshOperationStatuses = useCallback(() => {
    if (timeoutId) clearTimeout(timeoutId);
    setTimeoutId(null);
    setLastOperationStatusUpdateInitialValue(lastOperationStatusUpdated);
    setOperationStatusUpdatedAgo(0);
    refreshOperationStatuses();
  }, [lastOperationStatusUpdated, refreshOperationStatuses, timeoutId]);

  const updateOperationStatusUpdatedAgo = useCallback((lastUpdated: string) => {
    const ago = Math.floor((new Date().valueOf() - new Date(lastUpdated).valueOf()) / 1000 / 60);
    setOperationStatusUpdatedAgo(ago);

    const tid = setTimeout(() => {
      updateOperationStatusUpdatedAgo(lastUpdated);
    }, 20 * 1000);
    setTimeoutId(tid);

    if (ago > 30) onClickRefreshOperationStatuses();
  }, [onClickRefreshOperationStatuses]);

  useEffect(() => {
    if (!lastOperationStatusUpdated) return;

    if (lastOperationStatusUpdateInitialValue === null) {
      setLastOperationStatusUpdateInitialValue(lastOperationStatusUpdated);
    } else if (lastOperationStatusUpdateInitialValue !== lastOperationStatusUpdated) {
      updateOperationStatusUpdatedAgo(lastOperationStatusUpdated);
    }
  }, [lastOperationStatusUpdateInitialValue, lastOperationStatusUpdated, updateOperationStatusUpdatedAgo]);

  const [canSendMail, setCanSendMail] = useState<boolean>(false);
  const [sendMailDialogIsOpen, setSendMailDialogIsOpen] = useState<boolean>(false);

  const sendMailIconOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '配車指示メール' });
    setSendMailDialogIsOpen(true);
  }, []);

  const sendMailDialogClose = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_MAIL, button_name: 'キャンセル' });
    setSendMailDialogIsOpen(false);
  }, []);

  const sendMailButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_MAIL, button_name: '送信する' });
    sendOperationMail();
    sendMailDialogClose();
  }, [sendMailDialogClose, sendOperationMail]);

  useEffect(() => {
    if (!driverApp) return;
    setCanSendMail(driverApp && selectedDeliveryIds.length > 0);
  }, [driverApp, selectedDeliveryIds.length]);

  const sendMailMemo = useMemo(() => (
    canSendMail && (
      <IconButton
        onClick={sendMailIconOnClick}
        disabled={!!currentHistoryVersion}
      >
        <MobileScreenShareIcon />
      </IconButton>
    )
  ), [canSendMail, currentHistoryVersion, sendMailIconOnClick]);

  const printingMemo = useMemo(() => (
    <IconButton
      onClick={selectPrintButtonOnClick}
      disabled={isLoading || !!currentHistoryVersion}
      sx={{ pl: 0.5 }}
    >
      <Tooltip
        title="配送指示書を出力する"
        arrow
      >
        <LocalPrintshopRoundedIcon />
      </Tooltip>
    </IconButton>
  ), [currentHistoryVersion, isLoading, selectPrintButtonOnClick]);

  const deliveryActionButtons = useMemo(() => (
    <Stack direction="row" gap={1} sx={{ mr: 1 }} justifyContent="flex-end">
      <Button
        size="small"
        variant="outlined"
        color="primary"
        loading={isConfirming || isUnconfirming}
        onClick={() => onUnconfirmDeliveries(selectedDeliveryIds)}
        disabled={!!currentHistoryVersion || selectedDeliveryIds.length === 0}
      >
        確定解除
      </Button>
      <Button
        size="small"
        variant="contained"
        color="primary"
        loading={isConfirming || isUnconfirming}
        onClick={() => onConfirmDeliveries(selectedDeliveryIds)}
        disabled={!!currentHistoryVersion || selectedDeliveryIds.length === 0}
      >
        配車確定
      </Button>
    </Stack>
  ), [selectedDeliveryIds, isConfirming, isUnconfirming, onConfirmDeliveries, onUnconfirmDeliveries, currentHistoryVersion]);

  const driverAppMemo = useMemo(() => (
    driverApp && (
      <Dialog
        open={sendMailDialogIsOpen}
        onClose={sendMailDialogClose}
      >
        <Stack
          sx={{
            p: 2
          }}
          spacing={1}
        >
          <Stack>
            <Typography variant="h4">
              ドライバーに配送指示メールを送信します。よろしいですか？
            </Typography>
          </Stack>
          <Divider />
          <Stack direction="row">
            <Button
              size="small"
              variant="contained"
              sx={{ width: '60%', marginRight: '10pt' }}
              loading={isLoading}
              onClick={sendMailButtonOnClick}
            >
              送信する
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{ width: '40%' }}
              loading={isLoading}
              onClick={sendMailDialogClose}
            >
              キャンセル
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    )
  ), [driverApp, isLoading, sendMailButtonOnClick, sendMailDialogClose, sendMailDialogIsOpen]);

  return (
    <Stack direction="row" gap={0} sx={{ width: '100%' }}>
      <Stack sx={{ width: '100%' }}>
        <Stack direction="row" gap={1} justifyContent="space-between" sx={{ '& .MuiTypography-root': { fontSize: 13 } }}>
          <Stack>
            <Stack direction="row" gap={1}>
              <Typography>
                {totalOrdersCount}
              </Typography>
              <Typography>
                {totalLoadQuantity}
              </Typography>
              <Typography>
                {totalLoadWeightKg}
              </Typography>
              <Typography>
                {totalLoadVolumeM3}
              </Typography>
          </Stack>
          <Stack direction="row" gap={1}>
              <Typography>
                {quantityLoadingRate}
              </Typography>
              <Typography>
                {weightLoadingRate}
              </Typography>
              <Typography>
                {volumeLoadingRate}
              </Typography>
            </Stack>
            <Stack direction="row" gap={1}>
              <Typography whiteSpace="nowrap">
                {utilizationRate}
              </Typography>
              <Typography whiteSpace="nowrap">
                {totalDistanceKm}
              </Typography>
              <Typography whiteSpace="nowrap">
                {co2}
              </Typography>
            </Stack>
          </Stack>
          <Stack sx={{ marginX: 0 }} direction="row" alignItems="flex-start">
            {sendMailMemo}
            {printingMemo}
          </Stack>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
          <Stack gap={0}>
            <Stack direction="row" gap={2}>
              <Typography>{['請求', `¥${numberDecorator.formatNumber(billing)}`].join(' ')}</Typography>
              <Typography>{['支払', `¥${numberDecorator.formatNumber(payment)}`].join(' ')}</Typography>
            </Stack>
            <Stack>
              <Typography>{['粗利', `¥${numberDecorator.formatNumber(profit)}`].join(' ')}</Typography>
            </Stack>
          </Stack>
          <IconButton
            onClick={onClickTariffCalculation}
            disabled={isLoading || !!currentHistoryVersion || selectedDeliveryIds.length === 0}
            sx={{ mr: 1 }}
          >
            <Tooltip title="運賃計算" arrow>
              <CalculateOutlined />
            </Tooltip>
          </IconButton>
        </Stack>
        <Divider sx={{ my: 1 }} />
        {driverApp && (
          <Stack
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            alignContent="center"
            direction="row"
            gap={1}
          >
            {operationStatusUpdatedAgo > 0 && (
              <Stack direction="row">
                <Typography variant="body2" fontSize={13}>
                  {operationStatusUpdatedAgo}
                  分前に更新あり
                </Typography>
                <Button size="small" sx={{ minWidth: 30, p: 0, m: 0 }} startIcon={<CachedIcon />} onClick={onClickRefreshOperationStatuses} />
              </Stack>
            )}
            {(operationStatusUpdatedAgo === 0 && lastOperationStatusUpdateInitialValue) && (
              <Stack>
                <Typography variant="body2" fontSize={13}>
                  配送状況最終更新：
                  {format(new Date(lastOperationStatusUpdateInitialValue), 'MM/dd HH:mm', { timeZone: 'Asia/Tokyo' })}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        {deliveryActionButtons}
      </Stack>
      {driverAppMemo}
    </Stack>
  );
});

export default PlanningStatisticsPresenter;
